import { tallyTokens } from "../../theme.tsx";
import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
} from "@mui/material";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import CreatePartnerCompanyDialog from "../partner/CreatePartnerCompanyDialog";
import {
  PartnerCompany,
  createPartnerApiClient,
  fetchPartnerCompanies,
  fetchPartnerContext,
  fetchPartnerLoginAsUrl,
  getCurrentTenantId,
  hasPartnerSession,
  applyPartnerLoginUrl,
  restorePartnerSession,
} from "../partner/partnerApi";

const PARTNER_ACCOUNT_VALUE = "__partner_account__";
const CREATE_COMPANY_VALUE = "__create_company__";
const COMPANY_MENU_VISIBLE_ROWS = 5;
const COMPANY_MENU_ITEM_HEIGHT = 36;
const COMPANY_MENU_STATIC_HEIGHT = 110;

/** Two letters for the avatar: the first of the first two words, so
 *  "Nordisk Byg A/S" reads NB. */
const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter((word) => /^[\p{L}\p{N}]/u.test(word))
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

/** Up and down chevrons: the row opens a list to switch between. */
const SwitchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <SvgIcon
    viewBox="0 0 24 24"
    className={className}
    sx={{
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
  >
    <path d="m8 9 4-4 4 4M16 15l-4 4-4-4" />
  </SvgIcon>
);

/** Menu rows, sized like the drawer's conversation rows. */
const menuItemSx = {
  fontSize: "13.5px",
  lineHeight: 1.35,
  minHeight: 0,
  padding: "6.5px 10px",
  borderRadius: `${tallyTokens.radius.sm}px`,
  color: tallyTokens.ink2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
  "&:hover": {
    backgroundColor: tallyTokens.surface3,
    color: tallyTokens.ink,
  },
  "&.Mui-selected, &.Mui-selected:hover": {
    backgroundColor: tallyTokens.brandWash,
    color: tallyTokens.brandInk,
    fontWeight: 600,
  },
  "&.Mui-focusVisible": {
    backgroundColor: tallyTokens.surface3,
  },
};

const clearCurrentConversationUrl = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const PartnerCompanySwitcher: React.FC = () => {
  const [companies, setCompanies] = useState<PartnerCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [visible, setVisible] = useState(false);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");

  const currentTenantId = getCurrentTenantId();
  const hasStoredPartnerSession = hasPartnerSession();
  const normalizedCompanySearch = companySearch.trim().toLowerCase();
  const companyMenuMaxHeight =
    COMPANY_MENU_VISIBLE_ROWS * COMPANY_MENU_ITEM_HEIGHT +
    COMPANY_MENU_STATIC_HEIGHT;
  const sortedCompanies = React.useMemo(
    () =>
      [...companies].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      ),
    [companies],
  );
  const filteredCompanies = normalizedCompanySearch
    ? sortedCompanies.filter((company) =>
        company.name.toLowerCase().includes(normalizedCompanySearch),
      )
    : sortedCompanies;

  const fetchPartnerData = React.useCallback(async () => {
    const partnerApi = createPartnerApiClient();
    try {
      setCompanies(await fetchPartnerCompanies(partnerApi));
      setVisible(true);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Partner token expired — clean up and hide switcher
        localStorage.removeItem("partnerToken");
      }
      setVisible(false);
      setLoading(false);
      return;
    }

    try {
      const partner = await fetchPartnerContext(partnerApi);
      setPartnerName(partner?.name ?? null);
    } catch {
      setPartnerName(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartnerData();
  }, [fetchPartnerData]);

  // Restore the partner account, open create company, or switch into a selected company tenant.
  const handleSwitch = async (value: string) => {
    if (value === currentTenantId || switching) return;

    if (value === PARTNER_ACCOUNT_VALUE) {
      if (restorePartnerSession()) {
        clearCurrentConversationUrl();
        window.location.reload();
        return;
      }
    }

    if (value === CREATE_COMPANY_VALUE) {
      setCreateDialogOpen(true);
      return;
    }

    // Request a login-as URL for the selected company, store the returned token
    // as the active session, then reload so the app boots in that company context.
    setSwitching(true);
    try {
      const partnerApi = createPartnerApiClient();
      const url = await fetchPartnerLoginAsUrl(partnerApi, value);
      if (url && applyPartnerLoginUrl(url)) {
        clearCurrentConversationUrl();
        window.location.reload();
        return;
      }
    } catch {
      // Silently fail — user stays on current company
    }
    setSwitching(false);
  };

  if (!visible) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py="6px">
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: "3px" }}>
      <Select
        value={currentTenantId ?? ""}
        onChange={(e) => handleSwitch(e.target.value as string)}
        onClose={() => setCompanySearch("")}
        displayEmpty
        renderValue={(selected) => {
          const partnerLabel = partnerName || i18n._(t`Partner account`);
          const currentCompany = selected
            ? companies.find((company) => company.tenant_id === selected)
            : undefined;
          // The row names where you are; the line under it says whose
          // account that is, since the company data carries no CVR number.
          const title = selected
            ? (currentCompany?.name ?? selected)
            : partnerLabel;
          const subtitle = selected ? partnerLabel : i18n._(t`Partner account`);

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "9px",
                minWidth: 0,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "9px",
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: tallyTokens.brand,
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                {getInitials(title)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: "13.5px",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: tallyTokens.ink,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </Box>
                {subtitle !== title && (
                  <Box
                    sx={{
                      fontSize: "11.5px",
                      lineHeight: 1.25,
                      color: tallyTokens.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {subtitle}
                  </Box>
                )}
              </Box>
            </Box>
          );
        }}
        IconComponent={SwitchIcon}
        size="small"
        fullWidth
        disabled={switching}
        sx={{
          borderRadius: `${tallyTokens.radius.md}px`,
          backgroundColor: "transparent",
          transition: "background-color 0.15s ease",
          "&:hover": { backgroundColor: tallyTokens.surface3 },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-select": {
            // The draft's row: 7px by 10px, with room kept for the chevron.
            padding: "7px 32px 7px 10px !important",
            minHeight: "0 !important",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiSelect-icon": {
            fontSize: "14px",
            right: "10px",
            top: "calc(50% - 7px)",
            color: tallyTokens.ink3,
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              backgroundColor: tallyTokens.surface,
              color: tallyTokens.ink,
              maxWidth: 260,
              maxHeight: `${companyMenuMaxHeight}px`,
              overflowY: "auto",
              mt: "-6px",
              padding: "6px",
              border: `1px solid ${tallyTokens.line}`,
              // A string, not a number: MUI multiplies bare numbers by the
              // theme's 12px radius, which made this a pill.
              borderRadius: `${tallyTokens.radius.md}px`,
              boxShadow: tallyTokens.shadow.lg,
              "& .MuiList-root": {
                padding: 0,
              },
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: {
              padding: 0,
            },
          },
        }}
      >
        <ListSubheader
          disableSticky
          sx={{
            bgcolor: "transparent",
            padding: "0 0 6px",
            lineHeight: "normal",
          }}
        >
          <TextField
            value={companySearch}
            onChange={(event) => setCompanySearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            size="small"
            autoFocus
            fullWidth
            placeholder="Search"
            inputProps={{
              "aria-label": "Search companies",
            }}
            sx={{
              // The drawer's search field: surface, hairline, 12px corners.
              "& .MuiOutlinedInput-root": {
                borderRadius: `${tallyTokens.radius.md}px`,
                backgroundColor: tallyTokens.surface,
                fontSize: "13.5px",
                "& fieldset": {
                  border: `1px solid ${tallyTokens.line}`,
                },
                "&:hover fieldset": {
                  borderColor: tallyTokens.lineStrong,
                },
                "&.Mui-focused fieldset": {
                  border: `1px solid ${tallyTokens.brand}`,
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "7px 10px",
                "&::placeholder": { color: tallyTokens.ink3, opacity: 1 },
              },
            }}
          />
        </ListSubheader>
        {hasStoredPartnerSession && (
          <MenuItem value={PARTNER_ACCOUNT_VALUE} sx={menuItemSx}>
            {partnerName || i18n._(t`Partner account`)}
          </MenuItem>
        )}
        {filteredCompanies.map((company) => (
          <MenuItem
            key={company.tenant_id}
            value={company.tenant_id}
            sx={menuItemSx}
          >
            {company.name}
          </MenuItem>
        ))}
        <Box
          component="li"
          role="separator"
          sx={{
            listStyle: "none",
            height: "1px",
            margin: "6px 4px",
            backgroundColor: tallyTokens.line,
          }}
        />
        <MenuItem
          value={CREATE_COMPANY_VALUE}
          sx={{
            ...menuItemSx,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: tallyTokens.brandInk,
            fontWeight: 600,
          }}
        >
          <SvgIcon
            viewBox="0 0 24 24"
            sx={{
              fontSize: 15,
              fill: "none",
              stroke: "currentColor",
              strokeWidth: 2,
              strokeLinecap: "round",
            }}
          >
            <path d="M12 5v14M5 12h14" />
          </SvgIcon>
          <Trans context="Partner portal button and dialog title for creating a company">
            Create Company
          </Trans>
        </MenuItem>
      </Select>
      <CreatePartnerCompanyDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={fetchPartnerData}
      />
    </Box>
  );
};

export default PartnerCompanySwitcher;
